import { ReactNode } from 'react';

import { Box, BoxProps } from '@rover/kibble/core';

type ShimmerRegionProps = {
  /** Whether the region is currently loading. Controls aria-busy and and if shimmers or content are rendered. */
  isLoading: boolean;
  /** Content to render while loading (e.g. shimmer placeholders). */
  shimmer: ReactNode;
  /** Content to render when loading is complete. */
  content: ReactNode;
} & Omit<BoxProps, 'children'>;

/**
 * An accessible wrapper for shimmer/loading content.
 *
 * Provides an `aria-live="polite"` region with `aria-busy` toggled by `isLoading`.
 *
 * @example
 * <ShimmerRegion
 *   isLoading={isLoading}
 *   shimmer={
 *     <>
 *       <TypographyShimmer type="heading" size="300" />
 *       <TypographyShimmer type="text" size="200" />
 *     </>
 *   }
 *   content={<ActualContent />}
 * />
 */
const ShimmerRegion = ({ isLoading, shimmer, content, ...rest }: ShimmerRegionProps): ReactNode => {
  if (!isLoading && !content) return null;

  return (
    <Box
      testID="shimmer-region"
      aria-busy={isLoading}
      aria-live="polite"
      {...rest}
      webProps={{
        role: 'region',
        ...rest.webProps,
      }}
    >
      {isLoading ? shimmer : content}
    </Box>
  );
};

export default ShimmerRegion;
